import { AccountService } from "../../../shared/services/account.service";
import { ActivatedRoute, Router } from "@angular/router";
import { AppSpinnerModule } from "../../../shared/modules/app-spinner.module";
import { Commission, CommissionV2 } from "../../../shared/models/commission.model";
import { CommissionInfo } from "../../../shared/models/commission-info.model";
import { CommissionService } from "../../../shared/services/commission.service";
import { Component, OnInit } from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { first, switchMap } from "rxjs/operators";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Observable, firstValueFrom, of } from "rxjs";
import { StampV2 } from "../../../shared/models/stamp.model";
import { State } from "../../../shared/services/state";
import { StateRuleModel } from "../../../shared/models/state-rules.model";
import { StateRulesService } from "../../../shared/services/state-rules.service";
import { StatesService } from "../../../shared/services/states.service";
import { ToastrService } from "ngx-toastr";
import { AddSignatureComponent } from "./components/add-signature/add-signature.component";
import { AddDigitalCertificate } from "./components/add-digital-certificate/add-digital-certificate.component";
import { CommissionFormModalComponent } from "./components/commission-form-modal/commission-form-modal.component";
import { NotaryStampService } from "../../../shared/services/notary-stamp.service";
import { AccountSignatureService } from "../../../shared/services/account-signature.service";
import { AppDialogModule } from "src/app/shared/components/app-dialog/app-dialog.component";
import { SassCertificateModel } from "src/app/shared/models/sassCertificate.model";
import { CertificateService } from "src/app/shared/services/certificate.service";
import { SupportContextService } from "../../../shared/services/support-context.service";
import { catchError, tap } from "rxjs/operators";

@Component({
    selector: "app-state-commission-page",
    templateUrl: "./state-commission-page.component.html",
    styleUrls: ['./state-commission-page.component.scss'],
    standalone: false
})
export class StateCommissionPage implements OnInit {
  selectedState: State;
  selectedRule: Observable<StateRuleModel | undefined>;
  selectedCommission: Observable<Commission | CommissionInfo | CommissionV2>;
  accountId: number;
  isCommissionExpired: boolean;
  isDraftCommission: boolean;
  isSignatureRequired : boolean;
  commissionId;
  sassGuid;
  isReadDataFromSassEnabled: boolean;
  certificates: SassCertificateModel[] = [];
  primaryStamp: StampV2 | undefined;
  ipenStamp: StampV2| undefined;
  ronStamp: StampV2| undefined;

  hasCommission: Observable<boolean> = of(false);
  ruleLoaded: Observable<boolean> = of(false);

  validationMessages = {
    MissingCertificate: 'Missing Certificate',
    MissingSignature: 'Missing Signature',
    MissingCommissionNumber: 'Missing Commission Number',
    StateVerificationFailed: 'State Verification Failed'
  };

  constructor(
    private readonly accountService: AccountService,
    private readonly accountSignatureService: AccountSignatureService,
    private readonly commissionService: CommissionService,
    private readonly stampService: NotaryStampService,
    private readonly modalService: NgbModal,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly spinner: AppSpinnerModule,
    private readonly stateRulesService: StateRulesService,
    private readonly statesService: StatesService,
    private readonly toastr: ToastrService,
    private readonly dialog: AppDialogModule,
    private readonly certificateService: CertificateService,
    public readonly supportContext: SupportContextService,
  ) { }

  ngOnInit() {
    this.spinner.show();
    this.isReadDataFromSassEnabled = this.route.snapshot.data['isReadDataFromSassEnabled'];
    const stateCode = this.getStateCode();
    this.selectedState = this.getStateByStateCode(stateCode);
    this.setSelectedStateRule(stateCode);
    this.getAccountId();
    if (this.isReadDataFromSassEnabled) {
      this.getCertificatesForCurrentUser();
    }
  }

  getCertificatesForCurrentUser(): void {
      this.certificateService.getCurrentAccountCertificates()
        .pipe(
          tap((response) => {
            this.certificates = response.certificates;
          }),
          catchError(() => {
            return [];
          }
          )).subscribe();
    }

  getAccountId() {
    if (this.supportContext.isActive && this.supportContext.accountId) {
      this.accountId = this.supportContext.accountId;
    } else {
      this.accountService.getCurrentAccount().subscribe((account) => {
        this.accountId = account.accountId;
      });
    }
  }

  setSelectedCommission(commissionStateCode: string) {
    this.commissionService.getCommissions(this.isReadDataFromSassEnabled).subscribe((commissions) => {
      const activeCommission = commissions.find((commission) => commission.stateCode === commissionStateCode);
      if (activeCommission) {
        if (this.isReadDataFromSassEnabled && this.isSignatureRequired) {
          this.accountSignatureService.getSelectedSignatureByCommission(activeCommission.sassGuid ?? "").subscribe(
            (response) => {
              activeCommission.signature = response.accountSignature?.signatureImageBase64 || null;
            }
          )
        }
        this.selectedCommission = of(activeCommission);
        this.commissionId = activeCommission.id;
        this.sassGuid = activeCommission.sassGuid;
        this.isDraftCommission = false;
        this.hasCommission = of(true);
        
        // Handle stamp differences between V1 and V2
        if ('stampList' in activeCommission) {
          const commissionV2 = activeCommission as CommissionV2;
          this.primaryStamp = commissionV2.stampList.find(stamp => stamp.orderType === "Any");
          this.ipenStamp = commissionV2.stampList.find(stamp => stamp.orderType === "IPEN");
          this.ronStamp = commissionV2.stampList.find(stamp => stamp.orderType === "RON");
        }
        
        this.checkIfCommissionExpired();
        this.spinner.hide();
        return;
      }

      if (this.isReadDataFromSassEnabled) {
        this.spinner.hide();
        if (!this.selectedCommission) {
            this.openCommissionModal("add");
        }
        return;
      }
      
      this.commissionService.getDraftCommissions().subscribe((commissionInfos) => {
        const draftCommission = commissionInfos.commissions.find((commissionInfo) => commissionInfo.stateCode === commissionStateCode);
        this.spinner.hide();
        if (draftCommission) {
          this.stampService.retrieveStampFile(draftCommission.stampImageFilePathId).subscribe(
            (response) => {
              draftCommission.stamp = response.stamp.base64String;
            }
          );
          if (draftCommission.accountSignatureId) {
            this.accountSignatureService.getSignatureById(draftCommission.accountSignatureId).subscribe(
              (response) => {
                draftCommission.signature = response.accountSignature?.signatureImageBase64 || null;
              }
            )
          }
          this.selectedCommission = of(draftCommission);
          this.checkIfCommissionExpired();
          this.commissionId = draftCommission.commissionInfoId;
          this.isDraftCommission = true;
          this.hasCommission = of(true);
        }
        if (!this.selectedCommission) {
          this.openCommissionModal("add");
        }
      });
    });
  }

  setSelectedStateRule(commissionStateCode: string) {
    this.stateRulesService.getRulesByStateCode(commissionStateCode).subscribe((response) => {
      if (response && response.stateRules) {
        this.selectedRule = of(response.stateRules);
        this.isSignatureRequired = response.stateRules.isAutoGeneratedSignatureAllowed || response.stateRules.isSignatureUploadEnabled;
        this.ruleLoaded = of(true);
        this.setSelectedCommission(commissionStateCode);        
      } else {
        this.toastr.error('Error loading state requirements, please contact Support');
        this.returnHome();
        this.spinner.hide();
      }      
    });
  }


  getStateByStateCode(stateCode: string): State {
    const commissionState = this.statesService.getStateByCode(stateCode);
    if (!commissionState) {
      throw new Error("State not found");
    }
    return commissionState;
  }

  getSanitizedImage(image: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      "data:image/jpg;base64," + image
    );
  }

  async openCommissionModal(mode: "add" | "edit") {
    let commission;
    if (this.selectedCommission) {
      commission = await firstValueFrom(this.selectedCommission);
    }
    else {
      commission = new CommissionInfo();
      commission.commissionStateCode = this.selectedState.code;
    }
    
    const modalRef = this.modalService.open(CommissionFormModalComponent, {
      size: "lg",
      backdrop: "static",
      keyboard: false,
    });
    modalRef.componentInstance.selectedRule = this.selectedRule;
    modalRef.componentInstance.mode = mode;
    modalRef.componentInstance.commission = commission;
    modalRef.componentInstance.commissionState = this.selectedState;
    modalRef.componentInstance.isReadDataFromSassEnabled = this.isReadDataFromSassEnabled;
    modalRef.componentInstance.currentAccountId = this.accountId;

    modalRef.result.then((result) => {
      if (result === "commissionAdded") {
        this.reloadCommissions();
      }
      else if (this.selectedCommission == null) {
        this.returnHome();
      }
    });
  }

  returnHome() {
    this.router.navigate(["/myaccount/manage-commissions/"]);
  }

  async openSignatureModal(mode: "add" | "edit") {
    const commission: Commission | CommissionInfo | CommissionV2 | undefined = await firstValueFrom(this.selectedCommission);
    const modalRef = this.modalService.open(AddSignatureComponent, {
      size: "full-screen",
      backdrop: "static",
      keyboard: false,
    });
    modalRef.componentInstance.selectedRule = this.selectedRule;
    modalRef.componentInstance.mode = mode;
    modalRef.componentInstance.commission = commission;
    modalRef.componentInstance.isReadDataFromSassEnabled = this.isReadDataFromSassEnabled;
    modalRef.componentInstance.currentAccountId = this.accountId;

    modalRef.result.then((result) => {
      if (result === "signatureAdded") {
        this.reloadCommissions();
      }
    });
  }

  async openCertificateModal(mode: "add" | "edit") {
    const commission: Commission | CommissionInfo | CommissionV2 | undefined = await firstValueFrom(this.selectedCommission);
    const modalRef = this.modalService.open(AddDigitalCertificate, {
      size: "lg",
      backdrop: "static",
      keyboard: false,
    });
    modalRef.componentInstance.mode = mode;
    modalRef.componentInstance.commission = commission;
    modalRef.componentInstance.selectedRule = this.selectedRule;
    modalRef.componentInstance.isReadDataFromSassEnabled = this.isReadDataFromSassEnabled;
    modalRef.componentInstance.currentCertificateGuid = this.certificates && this.certificates.length != 0 ? this.certificates[0].certificateGuid : '';

    modalRef.result.then(() => {
      this.reloadCommissions();
      if (this.isReadDataFromSassEnabled) {
        this.getCertificatesForCurrentUser();
      }
    });
  }

  reloadCommissions() {
    this.spinner.show();
    this.setSelectedCommission(this.selectedState.code);
  }

  getStateCode(): string {
    const routeId = this.route.snapshot.paramMap.get('statecode');
    if (routeId === null || routeId === undefined) {
      return "";
    }
    return routeId;
  }

  deleteCommission() {
    this.dialog.show({
      title: "Delete Commission",
      message: `Are you sure you want to delete the ${this.selectedState.name} commission?`,
      noButtonText: "Cancel",
      yesButtonText: "Delete Commission",
    }).result.then((dialogResult) => {
      if (dialogResult === "yes") {
        if (!this.isDraftCommission) {
          this.selectedCommission.pipe(first(),
            switchMap(selected => this.commissionService.deleteCommission(this.isReadDataFromSassEnabled, selected as Commission))).subscribe(
              () => {
                this.toastr.success('Commission Deleted');
                this.returnHome();
              },
              () => {
                this.toastr.error('Commission Deletion Failed');
              }
          );
        }
        else {
          this.selectedCommission.pipe(first(),
            switchMap(() => this.commissionService.deleteDraftCommission(this.commissionId))).subscribe(
              () => {
                this.toastr.success('Draft Commission Deleted');
                this.returnHome();
              },
              () => {
                this.toastr.error('Draft Commission Deletion Failed');
              }
          );
        }
      }
    });
  }

  checkIfCommissionExpired(): void {
    this.selectedCommission.pipe(first()).subscribe(commission => {
      this.isCommissionExpired = new Date(commission.expiresOn).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
    });
  }

  hasActiveCertificate(commission: Commission | CommissionV2 ): boolean {
    if (this.isReadDataFromSassEnabled) {
      return !!(commission.certificate)
    }

    return !!(commission.certificateMetaDataId);
  }
}
